/**
 * #44 Yasaklı Kelimeler — ekip mesajlaşmasında maskelenecek kelimelerin sözlüğü.
 *
 * DAVRANIŞ TEK VE SABİTTİR: maskele ve gönder. Kelime başına ayrı davranış
 * (engelle/maskele/raporla) yoktur — iş sahibinin kararı budur. Mesaj engellenmez;
 * kelime * ile maskelenir, ham metin "Yazışma Denetimi" ekranında saklanır.
 *
 * Eşleşme KELİME BAZLIDIR (alt dize değil) ve Türkçe aksana duyarsızdır: "rakıp"
 * yazarak "rakip" tanımından kaçılamaz, "klasik" de "sik" tanımıyla bozulmaz.
 *
 * c360 liste altyapısı üzerine kuruludur (repo standardı).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import { useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon } from './c360';
import { usePageActions } from '../contexts/PermissionContext';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/teamchatbannedwords';
const LIST_ROUTE = '/team-chat-banned-words';

const COLUMN_FILTER_META = {
  word: { type: 'text', odataField: 'Word' },
  category: { type: 'text', odataField: 'Category' },
};

export function TeamChatBannedWordsPage() {
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  usePageMeta('Yasaklı Kelimeler', 'Ekip mesajlaşmasında maskelenen kelimeler.');

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const searchFields = useMemo(() => ['Word', 'Category'], []);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields,
    defaultSortField: 'Word',
    defaultSortDir: 'asc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openDetail = (row = selected, view = false) => {
    if (!row) return;
    navigate(`${LIST_ROUTE}/${row.id}${view ? '' : '?edit=1'}`, { state: { row, view } });
  };

  /**
   * FİZİKSEL silme. Geçmiş ihlal kayıtları etkilenmez: onlar yakalanan kelimeyi METİN
   * olarak saklar ve bu tanıma FK ile bağlı değildir. Geçici kapatmak isteyen "Pasif" yapar.
   */
  const handleDelete = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`"${row.word}" tanımı silinecek. Geçmiş ihlal kayıtları korunur. Onaylıyor musunuz?`)) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ id: row.id }),
      });
      if (!res.ok) throw new Error('Tanım silinemedi.');
      showToast('success', 'Tanım silindi.');
      fetchData();
    } catch (e) {
      showToast('error', e.message);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('word', {
      header: 'Kelime', meta: { filterType: 'text' },
      cell: (info) => <span className="c360-erp-chip">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('category', {
      header: 'Kategori', meta: { filterType: 'text' },
      cell: (i) => i.getValue() || '-',
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', enableColumnFilter: false,
      cell: (i) => (i.getValue() ? 'Aktif' : 'Pasif'),
    }),
    columnHelper.display({
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={s.iconBtn} title="İzle"
            onClick={(e) => { e.stopPropagation(); openDetail(row.original, true); }}>
            <ViewIcon />
          </button>
          {canEdit && (
            <button type="button" style={s.iconBtn} title="Düzenle"
              onClick={(e) => { e.stopPropagation(); openDetail(row.original, false); }}>
              <EditIcon />
            </button>
          )}
          {canDelete && (
            <button type="button" style={s.iconBtn} title="Sil"
              onClick={(e) => { e.stopPropagation(); handleDelete(row.original); }}>
              <DeleteIcon />
            </button>
          )}
        </div>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <ListPageShell
      title="Yasaklı Kelimeler"
      subtitle="Ekip mesajlaşmasında bu kelimeler maskelenerek gönderilir; ham metin Yazışma Denetimi'nde saklanır."
      search={{ value: filterInput, onChange: setFilterInput, onSubmit: applyFilterText, placeholder: 'Kelime veya kategori ara…' }}
      actions={(
        <>
          <ColumnChooser table={table} />
          <button type="button" style={s.secondaryBtn}
            onClick={() => exportRows().then((data) => exportToExcel(data, 'yasakli-kelimeler'))}>
            <ExcelIcon />Excel
          </button>
          {canAdd && (
            <button type="button" style={newRecordButtonStyle} onClick={() => navigate(`${LIST_ROUTE}/new`)}>
              Yeni Kelime
            </button>
          )}
        </>
      )}
    >
      <C360Table
        table={table}
        loading={loading}
        rows={rows}
        selectedId={selected?.id}
        onRowClick={(row) => setSelected(row)}
        onRowDoubleClick={(row) => openDetail(row, true)}
        sortField={toGridSortField(sortField)}
        sortDir={sortDir}
        onSort={(field) => applySort(toODataSortField(field))}
        columnFilters={columnFilters}
        onColumnFilter={setColumnFilter}
        onClearColumnFilter={clearColumnFilter}
        emptyText={filterText ? 'Aramaya uygun kelime yok.' : 'Henüz yasaklı kelime tanımlanmadı.'}
      />
      <Pagination
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
      <Toast toast={toast} />
    </ListPageShell>
  );
}
